/**
 * 會員端「(快) / (⑥)」額度欄位與後端多種命名對齊（與代理端用戶列表 map 邏輯一致）。
 * 《qqq的項目》member/info：data.member.wallets.kk（快開）、.hk（香港/⑥）；
 * member/accounts：type 0=快開額度、type 1=香港額度（見文檔示例）。
 */

function pickNestedNum(obj, parents, sub) {
  if (!obj || typeof obj !== 'object') return null
  for (let i = 0; i < parents.length; i++) {
    const nest = obj[parents[i]]
    if (nest && typeof nest === 'object' && !Array.isArray(nest)) {
      const v = nest[sub] ?? nest[`${sub}_quota`] ?? nest[`quota_${sub}`]
      if (v != null && String(v).trim() !== '') {
        const n = Number(v)
        if (Number.isFinite(n)) return n
      }
    }
  }
  return null
}

function num(v, fallback = 0) {
  if (v == null || v === '') return fallback
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

/**
 * @param {Record<string, unknown>} raw 登錄 data / member/info / 單條帳戶
 * @returns {{ quota: number, available: number, lhcQuota: number, lhcAvailable: number }}
 */
export function normalizeMemberCreditProfile(raw) {
  if (!raw || typeof raw !== 'object') {
    return { quota: 0, available: 0, lhcQuota: 0, lhcAvailable: 0 }
  }

  /** member/info：嵌套 member.wallets */
  let base = raw
  if (raw.member && typeof raw.member === 'object') {
    const m = raw.member
    base = { ...raw }
    const w = m.wallets
    if (w && typeof w === 'object') {
      const kk = w.kk || w.KK
      const hk = w.hk || w.HK
      if (kk) {
        if (kk.limit != null) base.quota_kc = kk.limit
        if (kk.balance != null) base.balance_kc = kk.balance
      }
      if (hk) {
        if (hk.limit != null) base.quota_lhc = hk.limit
        if (hk.balance != null) base.balance_lhc = hk.balance
      }
    }
    if (m.panlu != null && base.plate == null) base.plate = m.panlu
    if (m.account != null && base.username == null) base.username = m.account
  }

  const quotaKcVal =
    base.quota_kc ??
    base.credit_kc ??
    base.credit_k ??
    base.kc_quota ??
    base.k_credit ??
    base.quota ??
    base.credit ??
    pickNestedNum(base, ['quota', 'credit', 'limits'], 'kc')

  const quotaLhcVal =
    base.quota_lhc ??
    base.credit_lhc ??
    base.lhc_quota ??
    base.lhc_credit ??
    pickNestedNum(base, ['quota', 'credit', 'limits'], 'lhc')

  const balanceKcVal =
    base.balance_kc ??
    base.usable_kc ??
    base.kc_yue ??
    base.yue_kc ??
    base.money_kc ??
    base.balance ??
    base.available ??
    pickNestedNum(base, ['balance', 'yue', 'money'], 'kc')

  const balanceLhcVal =
    base.balance_lhc ??
    base.usable_lhc ??
    base.lhc_yue ??
    base.yue_lhc ??
    pickNestedNum(base, ['balance', 'yue', 'money'], 'lhc')

  return {
    quota: num(quotaKcVal),
    available: num(balanceKcVal),
    lhcQuota: num(quotaLhcVal),
    lhcAvailable: num(balanceLhcVal),
  }
}

/**
 * member/accounts 為多帳戶數組時，按類型拆到 kc / lhc 再規整。
 * @param {unknown[]} arr
 * @returns {Record<string, unknown>|null}
 */
export function mergeMemberAccountsArray(arr) {
  if (!Array.isArray(arr) || !arr.length) return null
  const flat = {}
  arr.forEach((item) => {
    if (!item || typeof item !== 'object') return
    const tag = String(
      item.type ?? item.kind ?? item.game_type ?? item.category ?? item.name ?? ''
    ).toLowerCase()
    const limitVal = item.maxLimit ?? item.quota ?? item.credit ?? item.limit
    const balVal = item.balance ?? item.usable ?? item.available ?? item.yue

    const tn = typeof item.type === 'number' ? item.type : parseInt(String(item.type), 10)
    const resVal = item.result ?? item.todayResult ?? item.today_win
    if (tn === 0) {
      if (limitVal != null) flat.quota_kc = limitVal
      if (balVal != null) flat.balance_kc = balVal
      if (resVal != null) flat.todayWin = resVal
      return
    }
    if (tn === 1) {
      if (limitVal != null) flat.quota_lhc = limitVal
      if (balVal != null) flat.balance_lhc = balVal
      if (resVal != null) flat.todayWin = resVal
      return
    }

    const isLhc =
      tag.includes('lhc') ||
      tag.includes('six') ||
      tag.includes('⑥') ||
      tag.includes('hk') ||
      tag === '2'
    const q = limitVal
    const b = balVal
    if (isLhc) {
      if (q != null) flat.quota_lhc = q
      if (b != null) flat.balance_lhc = b
    } else if (tag && !Number.isNaN(tn)) {
      if (q != null) flat.quota_kc = flat.quota_kc ?? q
      if (b != null) flat.balance_kc = flat.balance_kc ?? b
    }
  })
  return Object.keys(flat).length ? flat : null
}

/**
 * member/info 返回的 play_limits → 信用資料彈窗左右表
 * @param {unknown} playLimits
 * @returns {{ left: object[], right: object[] }}
 */
export function mapPlayLimitsToCreditRows(playLimits) {
  if (!Array.isArray(playLimits) || !playLimits.length) {
    return { left: [], right: [] }
  }
  const mapRow = (row) => ({
    name: String(row.type ?? row.name ?? '—'),
    odds: String(row.commission ?? row.odds ?? '—'),
    minBet: String(row.min_stake ?? row.minBet ?? '—'),
    maxBet: String(row.max_stake ?? row.maxBet ?? '—'),
    maxPeriod: String(row.max_period ?? row.maxPeriod ?? '—'),
  })
  const mapped = playLimits.map(mapRow)
  const mid = Math.ceil(mapped.length / 2)
  return {
    left: mapped.slice(0, mid),
    right: mapped.slice(mid),
  }
}

function firstNonEmptyStr(...vals) {
  for (let i = 0; i < vals.length; i++) {
    const v = vals[i]
    if (v == null) continue
    const s = String(v).trim()
    if (s) return s
  }
  return ''
}

/**
 * 底欄「今日輸贏」：member/info 等返回體常見字段（與後端命名寬鬆對齊）
 * @param {Record<string, unknown>|null} raw
 * @returns {string|null}
 */
export function pickTodayWinLossFromMemberRaw(raw) {
  if (!raw || typeof raw !== 'object') return null

  const tryKeys = [
    'today_win',
    'todayWin',
    'today_win_loss',
    'day_win',
    'dayWin',
    'win_today',
    'todayResult',
    'today_result',
    'jr_result',
    'profit_today',
    'todayProfit',
    'today_profit',
    'winLossToday',
    'day_result',
    'dresult',
    'Dresult',
    'dayResult',
    'win_loss_today',
    'today_amount',
    'todayAmount',
    'result',
  ]

  const formatOne = (v) => {
    if (v == null || v === '') return null
    const n = Number(String(v).replace(/[^\d.-]/g, ''))
    if (Number.isFinite(n)) return (Math.round(n * 100) / 100).toFixed(2)
    const s = String(v).trim()
    return s || null
  }

  const scan = (o) => {
    if (!o || typeof o !== 'object') return null
    for (let i = 0; i < tryKeys.length; i++) {
      const k = tryKeys[i]
      if (o[k] != null && String(o[k]).trim() !== '') {
        const t = formatOne(o[k])
        if (t != null) return t
      }
    }
    return null
  }

  let hit = scan(raw)
  if (hit) return hit
  const m = raw.member && typeof raw.member === 'object' ? raw.member : null
  if (m) {
    hit = scan(m)
    if (hit) return hit
    const nest = m.stats || m.dayStats || m.report || m.summary
    if (nest && typeof nest === 'object') {
      hit = scan(nest)
      if (hit) return hit
    }
  }
  const stats2 = raw.stats || raw.dayStats || raw.summary
  if (stats2 && typeof stats2 === 'object') {
    hit = scan(stats2)
    if (hit) return hit
  }
  return null
}

/**
 * 側欄頭部展示：優先暱稱，與帳號不同時第二行顯示帳號（member/info 或登錄 data 多種命名）
 * @param {Record<string, unknown>|null} raw
 * @param {string} [fallbackAccount]
 * @returns {{ primary: string, secondary: string }}
 */
export function pickMemberDrawerLabels(raw, fallbackAccount) {
  const fb = String(fallbackAccount || '').trim()
  if (!raw || typeof raw !== 'object') {
    return { primary: fb || '访客', secondary: '' }
  }
  const m = raw.member && typeof raw.member === 'object' ? raw.member : {}
  const nick = firstNonEmptyStr(
    m.nickname,
    m.nickName,
    m.name,
    m.Name,
    raw.nickname,
    raw.nickName,
    raw.name,
    raw.Name
  )
  const acct = firstNonEmptyStr(
    m.account,
    m.username,
    m.loginName,
    raw.account,
    raw.username,
    raw.loginName,
    fb
  )
  if (nick && acct && nick !== acct) {
    return { primary: nick, secondary: acct }
  }
  if (nick) {
    return { primary: nick, secondary: '' }
  }
  if (acct) {
    return { primary: acct, secondary: '' }
  }
  return { primary: fb || '访客', secondary: '' }
}

/** 手機信用頁 彩種標題繁簡映射 */
export const TITLE_RENAME_MAP = {
  '极速时时彩': '极速時時彩',
  '幸运飞艇': '幸運飛艇',
  '极速赛车': '极速賽車',
  '极速飞艇': '極速飛艇',
}

/** 手機信用頁 tableBox1 行結構 */
const NAME_RENAME_MAP = {
  '冠亚军和大小': '冠亞和大小',
  '冠亚军和单双': '冠亞和單雙',
  '冠亚和双面': '冠亞和雙面',
  '冠亚和': '冠亞和',
  '双面': '雙面',
  '两面': '兩面',
  '顺子': '順子',
  '对子': '對子',
  '半顺': '半順',
  '杂六': '雜六',
  '总合双面': '總合雙面',
  '龙虎和': '龍虎和',
  '两球双面': '兩球雙面',
  '牛牛双面': '牛牛雙面',
  '1-10名双面': '1-10名雙面',
  '1-5龙虎': '1-5龍虎',
  '1-10车号': '1-10單碼',
  '1-5球号': '1-5單碼',
}
export function mapPlayLimitsToMobiLimitRows(playLimits) {
  const { left, right } = mapPlayLimitsToCreditRows(playLimits)
  const all = [...left, ...right]
  return all.map((r) => ({
    type: NAME_RENAME_MAP[r.name] || r.name,
    a: r.odds,
    min: r.minBet,
    bet: r.maxBet,
    period: r.maxPeriod,
  }))
}
